import { ReactNode, RefObject } from 'react';
import clsx from 'clsx';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import { OpInfoTooltip } from '../OpInfoTooltip/OpInfoTooltip';

import './OpSection.scss';

interface OpSectionProps {
  children: ReactNode;
  label?: ReactNode;
  className?: string;
  disabled?: boolean;
  tooltip?: ReactNode;
  testId?: string;
  loading?: boolean;
  sectionHeightForLoader?: number;
  innerRef?: RefObject<HTMLDivElement>;
}

/**
 * OpSection is a composable component that provides a consistent layout for sections within the application.
 * It supports labels, tooltips, disabled states, and a loading indicator.
 *
 * @param {React.ReactNode} children - The content to be rendered within the section.
 * @param {string} label - The label to be displayed at the top of the section.
 * @param {string} className - Additional CSS classes to apply to the section.
 * @param {boolean} disabled - Whether the section is disabled.
 * @param {string} tooltip - The tooltip text to display when hovering over the info icon.
 * @param {boolean} loading - Whether to display a loading indicator.
 * @param {number} sectionHeightForLoader - The total height of the section once rendered, used to calculate the loading spinner height.
 * @param {string} testId - The test ID for the section.
 * @param {React.Ref<HTMLDivElement>} innerRef - A ref to the inner div element.
 * @returns {JSX.Element} A React element representing the OpSection.
 */
export const OpSection = ({
  children,
  label,
  className,
  disabled,
  tooltip,
  loading = false,
  sectionHeightForLoader = 300,
  testId = 'op-section',
  innerRef,
}: OpSectionProps) => {
  return (
    <div
      data-testid={testId}
      className={clsx(
        'op-section',
        { 'op-section--disabled': disabled },
        className,
      )}
      ref={innerRef}
    >
      {Boolean(label || tooltip) && (
        <div>
          {Boolean(label) && <span className="op-section__label">{label}</span>}
          {Boolean(tooltip) && (
            <OpInfoTooltip className="op-section__tooltip" title={tooltip} />
          )}
        </div>
      )}
      {loading ? (
        <div
          className="op-section__loading-wrapper"
          style={{ height: sectionHeightForLoader - 34 - (label ? 41 : 0) }}
        >
          <LoadingOutlined />
        </div>
      ) : (
        children
      )}
    </div>
  );
};
